import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormControl, FormArray, FormGroup, AbstractControl } from '@angular/forms';
import { Observable, throwError, merge } from 'rxjs';
import { map, distinct, mapTo, catchError, retry } from 'rxjs/operators';
import { EMPTY, empty, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { jsonheader } from '@app/shared/constants';
import { EventType, ObjectType } from '@app/shared/constants-server';
import { Sales_order, RentalDecisionPayload, RentalLifecycle } from '@app/shared/_models/sales';
import { ApiResponse } from '@app/shared/_models/common';
import { businessDayOf, dayNoonIso } from '@app/shared/time';

/**
 * Jour civil `aaaa-mm-jj` dans le fuseau LOCAL. toISOString() bascule d'abord en UTC :
 * une date choisie au calendrier (minuit local) y perdait un jour tout l'été
 * (01/08 minuit à Paris = 31/07 22h UTC), et la recherche s'arrêtait la veille.
 */
function toLocalDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export interface QuotationsFilter {
  // every field is string because it comes from html and is used in the url
  name?: string;
  state?: string;
  customer?: string;
  datstart?: Date;
  datend?: Date;
  dattyp?: string; // date type is consistent w/ state: 0: DELETED, 1: Created, 2: Quote, 3: Preparation, 4: Delivered, 5: Invoiced, 6: Payed, 7: Refused quote
  limit?: string;
  store?: string;
  user?: string;
  doc_type?: string;
  id?: string; // raccourci « Id commande » : envoyé seul, le serveur ignore alors tous les autres critères
}

@Injectable({
  providedIn: 'root',
})
export class QuotationsService {
  constructor(private http: HttpClient) {}

  // getQuotationsByFilter(name:string, state?:string) : Observable<any> { // Old way by cno change 28 feb 2021
  //   let q:String = "";
  //   if (name) q += "name=" + name;
  //   if (state && state!="-1") q = q + (q===""?"":"&") + "state=" + state;
  //   return this.http.get<void>(environment.server_url + "/sales/orders?" + q);
  //   }

  getQuotationsByFilter(filter: QuotationsFilter, options?: string): Observable<Sales_order[]> {
    let q_array = [];

    console.log('filter', filter);
    for (const key in filter) {
      if (filter[key as keyof QuotationsFilter] !== undefined && filter[key as keyof QuotationsFilter] !== null && filter[key as keyof QuotationsFilter] !== '' && filter[key as keyof QuotationsFilter] !== 'undefined' && filter[key as keyof QuotationsFilter]?.toString() !== '-1') {
        // if ( key === 'limit' && filter[key as keyof QuotationsFilter]  && ( (filter[key as keyof QuotationsFilter])!.toLowerCase() === 'infini'  || ( filter[key as keyof QuotationsFilter])!.toLowerCase() === 'infinie' ) ) continue;
        // console.log('filter[key as keyof QuotationsFilter]',filter[key as keyof QuotationsFilter]);
        if (filter[key as keyof QuotationsFilter] instanceof Date) {
          q_array.push(`${key}:${toLocalDay(filter[key as keyof QuotationsFilter] as Date)}`);
        } else {
          q_array.push(`${key}:${filter[key as keyof QuotationsFilter]?.toString() ?? ''}`);
        }
      }
    }
    let url = environment.server_url + '/sales/orders?q=' + q_array.join(' AND ');
    if (options) {
      url += '&o=' + options;
    }
    console.log('url', url);
    return this.http.get<Sales_order[]>(url);
  }

  getQuotationById(id: number): Observable<any> {
    return this.http.get<void>(environment.server_url + '/sales/orders?q=id:' + id + '&o=deliveries,lines');
  }

  /**
   * Déclenche l'événement de facturation périodique des locations (RENTAL_INVOICING).
   *
   * @param selectedOrderIds IDs des contrats de location à facturer
   * @param userId Identifiant de l'utilisateur courant
   * @param fseOrdersMapping Mapping { [order_id]: fse_id } des FSE enfants déjà sécurisées par le client Area (contrats FSE)
   * @param excludedLineIds Lignes de dossier décochées (sales_order_l.id) : ni facturées ni créditées à ce passage
   * @param asOf « Facturer jusqu'au » (« AAAA-MM-JJ », passé ou aujourd'hui) : jour du calcul du passage ; null = aujourd'hui
   */
  postRentalInvoicingEvent(
    selectedOrderIds: number[] | Set<number>,
    userId?: number,
    fseOrdersMapping?: Record<number, number>,
    excludedLineIds: number[] = [],
    asOf: string | null = null,
  ): Observable<any> {
    const ids = Array.isArray(selectedOrderIds) ? selectedOrderIds : Array.from(selectedOrderIds);
    const event = {
      id: 0,
      typ: EventType.RENTAL_INVOICING,
      userid: userId ?? 0,
      objectid: 0,
      objecttyp: ObjectType.ORDER,
      data: {
        selectedOrderIds: ids,
        ...(fseOrdersMapping && Object.keys(fseOrdersMapping).length > 0
          ? { fse_orders_mapping: fseOrdersMapping }
          : {}),
        ...(excludedLineIds.length > 0 ? { excluded_order_l_ids: excludedLineIds } : {}),
        ...(asOf ? { as_of: dayNoonIso(asOf) } : {}),
      },
    };
    return this.http.post<any>(`${environment.server_url}/event`, event, { headers: jsonheader });
  }

  // Décision de location (2102 interruption, geste commercial compris ; 2103 fenêtre) : jours en instants de Paris à midi.
  // Un refus métier revient en success = false avec son message.
  postRentalDecisionEvent(typ: EventType.RENTAL_INTERRUPTION | EventType.RENTAL_WINDOW, data: RentalDecisionPayload, userId?: number): Observable<ApiResponse<RentalDecisionResult>> {
    const event = { id: 0, typ, userid: userId ?? 0, objectid: data.order_id, objecttyp: ObjectType.ORDER, data };
    return this.http.post<ApiResponse<RentalDecisionResult>>(`${environment.server_url}/event`, event, { headers: jsonheader });
  }

  /**
   * Retourne les contrats de location via le endpoint dédié `GET /sales/rental_orders`.
   * Les lignes (avec `rental_lifecycle` par ligne) sont toujours incluses.
   */
  getRentalOrders(params: { store?: number; customer?: number; states?: string; limit?: number; returned?: boolean }): Observable<Sales_order[]> {
    const urlParams = new URLSearchParams();
    if (params.store !== undefined && params.store > 0) urlParams.set('store', params.store.toString());
    if (params.customer !== undefined && params.customer > 0) urlParams.set('customer', params.customer.toString());
    if (params.states) urlParams.set('states', params.states);
    if (params.limit !== undefined) urlParams.set('limit', params.limit.toString());
    if (params.returned === false) urlParams.set('returned', 'false'); // matériels repris non renvoyés
    const url = `${environment.server_url}/sales/rental_orders?${urlParams.toString()}`;
    return this.http.get<Sales_order[]>(url);
  }

  /** Cycle de vie d'un dossier de location (livraison, facturé jusqu'au, prochaine échéance, factures filles), calculé par le moteur de facturation. */
  // Jours reçus en instants de Paris, ramenés en « AAAA-MM-JJ » à la réception (lifecycleViewInDays).
  getRentalLifecycle(id: number): Observable<RentalLifecycleView> {
    return this.http.get<RentalLifecycleView>(`${environment.server_url}/sales/rental_orders/${id}/lifecycle`).pipe(map(lifecycleViewInDays));
  }

  /**
   * Évalue l'éligibilité et prépare les détails de facturation pour une sélection de contrats de location.
   *
   * @param orderIds Liste des IDs de contrats de location à analyser
   * @param excludedLineIds Lignes de dossier décochées dans le dialogue : écartées comme au passage, montants recalculés sans elles
   * @param asOf « Facturer jusqu'au » (« AAAA-MM-JJ », passé ou aujourd'hui) : ce qui est dû ce jour-là ; null = aujourd'hui
   * @returns Observable émettant la liste des contrats éligibles et des contrats non facturables
   */
  prepareRentalInvoicing(orderIds: number[], excludedLineIds: number[] = [], asOf: string | null = null): Observable<PrepareRentalInvoicingResponse> {
    const url = `${environment.server_url}/sales/rental_orders/prepare_invoicing`;
    const body = {
      order_ids: orderIds,
      ...(excludedLineIds.length > 0 ? { excluded_order_l_ids: excludedLineIds } : {}),
      ...(asOf ? { as_of: dayNoonIso(asOf) } : {}),
    };
    return this.http.post<PrepareRentalInvoicingResponse>(url, body, { headers: jsonheader }).pipe(map(preparedInDays));
  }
}

// Jour de Paris d'un instant reçu (businessDayOf, shared/time.ts), jour écrit à midi ; une valeur illisible passe telle quelle.
function dayIn(v: string): string {
  return businessDayOf(v) ?? v;
}

function dayOrNullIn(v: string | null | undefined): string | null {
  return v == null ? null : dayIn(v);
}

// Champ facultatif : converti s'il est présent, laissé absent sinon.
function optDayIn<K extends string>(key: K, v: string | null | undefined): { [P in K]?: string | null } {
  return (v === undefined ? {} : { [key]: dayOrNullIn(v) }) as { [P in K]?: string | null };
}

// Aperçu reçu en instants de Paris => jours « AAAA-MM-JJ », seuls lus par le dialogue de facturation et la FSE ; first_realdatdel reste l'instant réel du BL.
export function preparedInDays(r: PrepareRentalInvoicingResponse): PrepareRentalInvoicingResponse {
  const periodsIn = (list: PreparedSinglePeriodInfo[] | null | undefined) => (list ?? []).map((p) => ({ ...p, start: dayIn(p.start), end: dayIn(p.end) }));
  return {
    ...r,
    eligible_invoices: (r?.eligible_invoices ?? []).map((c) => ({
      ...c,
      as_of: dayIn(c.as_of), ...optDayIn('start', c.start), ...optDayIn('end', c.end), ...optDayIn('billed_until', c.billed_until),
      periods: (c.periods ?? []).map((p) => ({ ...p, start: dayIn(p.start), end: dayIn(p.end), ...(p.individual_periods ? { individual_periods: periodsIn(p.individual_periods) } : {}) })),
      ...(c.lines ? { lines: c.lines.map((l) => ({ ...l, ...optDayIn('start', l.start), ...optDayIn('end', l.end), individual_periods: periodsIn(l.individual_periods) })) } : {}),
      credits: (c.credits ?? []).map((x) => ({ ...x, start: dayOrNullIn(x.start), end: dayOrNullIn(x.end) })),
    })),
    unbillable_orders: r?.unbillable_orders ?? [],
  };
}

// Vue du cycle lue par la fiche de la pièce (quotationdetails) : mêmes jours convertis ; computed_at, realdatdel et return_realdatdel restent des instants réels.
export function lifecycleViewInDays(v: RentalLifecycleView): RentalLifecycleView {
  return {
    ...v,
    as_of: dayIn(v.as_of), planned_delivery_on: dayOrNullIn(v.planned_delivery_on),
    lines: (v.lines ?? []).map((l) => ({
      ...l,
      billed_until: dayOrNullIn(l.billed_until), due_start: dayOrNullIn(l.due_start), due_end: dayOrNullIn(l.due_end),
      ...(l.summary ? { summary: { ...l.summary, next_due_on: dayOrNullIn(l.summary.next_due_on) } } : {}),
    })),
    invoices: (v.invoices ?? []).map((i) => ({ ...i, invoiced_on: dayOrNullIn(i.invoiced_on), start: dayOrNullIn(i.start), end: dayOrNullIn(i.end) })),
  };
}

// Aperçu POST /sales/rental_orders/prepare_invoicing : le serveur écrit chaque jour en instant de Paris, à midi ;
// preparedInDays le ramène en jour « AAAA-MM-JJ » à la réception : ces types décrivent l'aperçu tel que le lisent le dialogue et la FSE.
// Toute plage start / end, premier et dernier jour INCLUS (avant le 27/10/2026 : period_from / period_to, fin exclue).

/** Détail d'une période unitaire découpée (semaine par semaine, mois par mois, etc.) : rang et jours du start au end inclus. */
export interface PreparedSinglePeriodInfo {
  rank: number; // avant : period_index
  start: string;
  end: string;
  label: string;
  uprice_taxx: number;
  uprice_taxi: number;
}

/** Ligne simulée de la facture à venir (article vendu ou prestation de location) */
export interface PreparedRentalSimulationLine {
  origin_order_l_id: number;
  line_type: 'RENTAL' | 'ITEM' | string;
  product_name: string;
  terms_name?: string | null; // intitulé du contrat de location de la ligne ; null pour une vente
  qty: number;
  periods_count?: number | null;
  start?: string | null; // premier jour couvert ; null pour une vente
  end?: string | null; // dernier jour couvert, inclus ; null pour une vente
  label: string;
  uprice_taxx: number;
  uprice_taxi: number;
  net_taxx: number;
  net_taxi: number;
  vat_rate: number;
  memo?: string | null;
  individual_periods: PreparedSinglePeriodInfo[];
}

/** Détail d'une période calculée pour un contrat éligible ; `periods_count` = rangs couverts, `qty` = quantité de la ligne (fraction au prorata, 2 décimales). */
export interface PreparedRentalPeriodInfo {
  origin_order_l_id: number;
  rank_end: number; // rang de la dernière période du groupe (avant : period_index)
  periods_count: number;
  qty: number;
  start: string; // premier jour couvert = BT-134 : dateDebut et dateExec de la FSE
  end: string; // dernier jour couvert, inclus = BT-135 : dateFin de la FSE
  label: string;
  fse_lpp?: string | null;
  base_fse_lpp?: string | null;
  uprice_taxx: number;
  uprice_taxi: number;
  individual_periods?: PreparedSinglePeriodInfo[];
}

/** Ligne d'avoir partiel prévue par le passage, telle qu'elle sera posée (quantité et montants négatifs). */
export interface PreparedRentalCredit {
  origin_order_l_id: number;
  corrects_invoice_id: number;
  corrects_doc_ref: string | null;
  corrects_line_id: number;
  label: string;
  qty: number;
  uprice_taxx: number;
  net_taxx: number;
  net_taxi: number;
  vat_rate: number;
  start: string | null; // premier jour corrigé ; null pour une vente annulée
  end: string | null; // dernier jour corrigé, inclus ; null pour une vente annulée
  fse_lpp?: string | null;
  memo?: string | null; // mémo de la ligne du dossier (patient, chambre), porté par la ligne d'avoir
}

/** Anomalie d'une ligne RENTAL (ou du dossier si origin_order_l_id est null) ; bloquante = la ligne n'est pas facturée. */
export interface RentalAnomaly {
  origin_order_l_id: number | null;
  blocked: boolean;
  message: string;
}

/** Réponse d'un évènement 2102, 2103 ou 2105 accepté : le cycle de la ligne tel qu'écrit (jours « AAAA-MM-JJ »). */
export interface RentalDecisionResult {
  order_id: number;
  line_id: number;
  decision_id: number;
  rental_lifecycle: RentalLifecycle;
}

/** Contrat de location éligible à la facturation */
export interface PreparedRentalContractInvoice {
  order_id: number;
  customer_id: number;
  customer_name: string;
  is_fse: boolean;
  fse_template_id?: number | null;
  amount_taxx: number;
  amount_taxi: number;
  credit_taxx: number; // avoirs prévus (≤ 0), en-têtes calculés par le serveur
  credit_taxi: number;
  lines_count: number;
  periods: PreparedRentalPeriodInfo[];
  start?: string | null; // premier jour du passage (plus petit start des périodes) ; avant : first_period_from / first_period_first_day
  end?: string | null; // dernier jour du passage, inclus (plus grand end) ; avant : last_period_to / last_period_last_day
  has_item_lines?: boolean;
  has_rental_lines?: boolean;
  first_realdatdel?: string | null; // instant réel du premier BL de livraison, au décalage de Paris (avant : first_deliv_date)
  billed_until?: string | null; // dernier jour déjà facturé, inclus (avant : last_billed_date, instant du lendemain)
  previous_invoiced_periods?: number;
  lines?: PreparedRentalSimulationLine[];
  credits: PreparedRentalCredit[];
  anomalies: RentalAnomaly[];
  as_of: string;
}

/** Contrat de location non facturable avec motif explicite */
export interface UnbillableRentalContract {
  order_id: number;
  customer_name: string;
  reason: string;
  anomalies: RentalAnomaly[];
}

/** Réponse de l'API de préparation / test d'éligibilité */
export interface PrepareRentalInvoicingResponse {
  eligible_invoices: PreparedRentalContractInvoice[];
  unbillable_orders: UnbillableRentalContract[];
}

/** Statut d'une ligne dans GET /sales/rental_orders/{id}/lifecycle (sales/src/rental/lifecycle.rs) : finished = Durée atteinte, closed = location close et soldée. */
export type RentalLineStatus = 'return' | 'not_started' | 'blocked' | 'up_to_date' | 'due' | 'interrupted' | 'finished' | 'closed';

/** Ligne RENTAL ou RENTAL_RETURN d’un dossier ; dates ramenées en jours de Paris « aaaa-mm-jj » à la réception (lifecycleViewInDays), sauf les instants réels du BL, plages start / end incluses. */
export interface RentalLineLifecycle {
  id: number;
  seq: number;
  typ: 'RENTAL' | 'RENTAL_RETURN';
  product: number;
  product_name: string | null;
  terms_name: string | null;
  period: number | null;
  realdatdel: string | null; // instant réel du BL de livraison, au décalage de Paris
  duration: number | null;
  rank: number;
  billed_periods: number;
  billed_until: string | null; // dernier jour facturé, inclus
  billed_until_source: 'mercalink' | 'orthop' | 'cycle' | null;
  status: RentalLineStatus;
  due_count: number;
  due_start: string | null; // premier jour échu non facturé
  due_end: string | null; // dernier jour échu non facturé, inclus
  summary?: { next_due_on: string | null } | null; // prochaine échéance (next_billable_on, son doublon, a disparu le 27/10/2026)
  lifecycle_unreadable: boolean;
  return_realdatdel?: string | null; // instant réel du BL de reprise, au décalage de Paris
  billed_quantity?: number;
  blocked?: boolean;
  anomalies?: string[];
}

/** Facture fille (FACTURE ou FACTURE_FSE, data.parent_order_id = dossier) et les périodes qu'elle couvre. */
export interface RentalChildInvoice {
  id: number;
  doc_type?: string;
  doc_ref: string | null;
  state: string;
  amount_taxx: number;
  amount_taxi: number;
  invoiced_on: string | null;
  corrects_invoice_id?: number | null;
  start: string | null; // premier jour couvert
  end: string | null; // dernier jour couvert, inclus
  periods: number;
}

export interface RentalLifecycleView {
  order_id: number;
  state: string;
  as_of: string;
  realdatdel: string | null; // instant réel du premier BL de livraison, au décalage de Paris
  planned_delivery_on: string | null;
  reprise: { dos_chrono?: number; dos_type?: string; livre_a_la_reprise?: boolean; [k: string]: any } | null;
  lines: RentalLineLifecycle[];
  invoices: RentalChildInvoice[];
  anomalies?: string[];
  computed_at: string;
}
